import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import SiteFooter from '../components/SiteFooter';
import DocGuideView from '../components/documentation/DocGuideView';
import DocModuleIcon from '../components/documentation/DocModuleIcon';
import DocModuleOverview from '../components/documentation/DocModuleOverview';
import { DOCUMENTATION_MODULES, GETTING_STARTED, PRODUCT_OVERVIEW } from '../data/documentationContent';
import { MODULE_SETUP_GUIDES } from '../data/moduleSetupGuides';
import {
  DOC_VIEWS,
  docPathGettingStarted,
  docPathGettingStartedGuide,
  docPathModule,
  docPathModuleGuide,
  docPathOverview,
  parseDocumentationPath,
} from '../data/documentationRoutes';
import './Documentation.css';

function NavChevron({ open }) {
  return (
    <svg
      className={`doc-nav-chevron ${open ? 'open' : ''}`}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const Documentation = () => {
  const location = useLocation();
  const parsed = useMemo(() => parseDocumentationPath(location.pathname), [location.pathname]);

  const [expanded, setExpanded] = useState(() => {
    const initial = new Set(['getting-started']);
    if (parsed.section && parsed.section !== 'getting-started') {
      initial.add(parsed.section);
    }
    if (parsed.moduleId) {
      initial.add(parsed.moduleId);
    }
    return initial;
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (parsed.section === 'getting-started') {
      setExpanded((prev) => new Set(prev).add('getting-started'));
    }
    if (parsed.moduleId) {
      setExpanded((prev) => new Set(prev).add(parsed.moduleId));
    }
  }, [parsed.section, parsed.moduleId]);

  const toggleSection = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isActivePath = (path) => location.pathname === path;

  const renderContent = () => {
    if (parsed.view === DOC_VIEWS.OVERVIEW) {
      return (
        <article className="doc-page-card doc-overview-card">
          <h2 className="doc-section-title">{PRODUCT_OVERVIEW.title}</h2>
          <p className="doc-lead">{PRODUCT_OVERVIEW.intro}</p>
          <div className="doc-highlight-grid">
            {PRODUCT_OVERVIEW.highlights.map((item) => (
              <div key={item.label} className="doc-highlight-item">
                <strong>{item.label}</strong>
                <span>{item.detail}</span>
              </div>
            ))}
          </div>
          <h3 className="doc-subheading">Core modules included</h3>
          <ul className="doc-module-pills">
            {PRODUCT_OVERVIEW.moduleNames.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
          <div className="doc-cta-banner">
            <div>
              <strong>Need step-by-step screenshots?</strong>
              <p className="mb-0">Our User Guides walk each role through every screen in the live app.</p>
            </div>
            <Link to="/help#user-guides" className="doc-cta-link">
              Open User Guides &rarr;
            </Link>
          </div>
        </article>
      );
    }

    if (parsed.view === DOC_VIEWS.GETTING_STARTED) {
      return (
        <article className="doc-page-card doc-getting-started-card">
          <span className="doc-guide-step-pill">Setup overview</span>
          <h2 className="doc-section-title">{GETTING_STARTED.title}</h2>
          <p className="doc-lead">{GETTING_STARTED.intro}</p>
          <h3 className="doc-subheading">Recommended setup order</h3>
          <ol className="doc-setup-roadmap">
            {GETTING_STARTED.recommendedOrder.map((label, index) => (
              <li key={label}>
                <span className="doc-roadmap-num">{index + 1}</span>
                <span>{label}</span>
              </li>
            ))}
          </ol>
          <h3 className="doc-subheading">Setup guides</h3>
          <div className="doc-setup-guide-grid">
            {GETTING_STARTED.guides.map((guide) => (
              <Link
                key={guide.id}
                to={docPathGettingStartedGuide(guide.id)}
                className="doc-setup-guide-card"
              >
                <span className="doc-setup-guide-card-num">{guide.stepNumber}</span>
                <div>
                  <h4>{guide.title}</h4>
                  <p>{guide.summary}</p>
                </div>
                <span className="doc-setup-guide-card-arrow" aria-hidden="true">
                  &rarr;
                </span>
              </Link>
            ))}
          </div>
        </article>
      );
    }

    if (parsed.view === DOC_VIEWS.GUIDE && parsed.section === 'getting-started' && parsed.guide) {
      return (
        <DocGuideView
          guide={parsed.guide}
          accent={parsed.guide.accent || '#667eea'}
          breadcrumb={
            <>
              <Link to={docPathGettingStarted()}>Getting Started</Link>
              <span>/</span>
              <span>{parsed.guide.title}</span>
            </>
          }
          backTo={docPathGettingStarted()}
          backLabel="Back to Getting Started overview"
        />
      );
    }

    if (parsed.view === DOC_VIEWS.MODULE && parsed.module) {
      const guides = MODULE_SETUP_GUIDES[parsed.module.id]?.guides || [];
      return (
        <DocModuleOverview
          module={parsed.module}
          guides={guides}
          setupIntro={MODULE_SETUP_GUIDES[parsed.module.id]?.overviewIntro}
        />
      );
    }

    if (parsed.view === DOC_VIEWS.GUIDE && parsed.module && parsed.guide) {
      return (
        <DocGuideView
          guide={parsed.guide}
          accent={parsed.module.accent}
          moduleId={parsed.module.id}
          breadcrumb={
            <>
              <Link to={docPathModule(parsed.module.id)}>{parsed.module.title}</Link>
              <span>/</span>
              <span>{parsed.guide.title}</span>
            </>
          }
          backTo={docPathModule(parsed.module.id)}
          backLabel={`Back to ${parsed.module.title} overview`}
        />
      );
    }

    return <Navigate to={docPathOverview()} replace />;
  };

  return (
    <div className="d-flex flex-column min-vh-100 documentation-page">
      <main className="flex-grow-1" style={{ paddingTop: '88px' }}>
        <div className="doc-shell">
          <Link to="/" className="doc-back-link">
            &larr; Back to Home
          </Link>

          <div className="doc-page-hero text-center">
            <span className="doc-hero-badge">Documentation</span>
            <h1>SkoolWeb Product Documentation</h1>
            <p className="mb-0 opacity-90">
              Module-wise setup guides for schools evaluating SkoolWeb before signup.
            </p>
          </div>

          <div className="doc-layout">
            <aside className="doc-sidebar-wrap">
              <nav className="doc-sidebar" aria-label="Documentation navigation">
                <p className="doc-sidebar-label">Navigate</p>

                <Link
                  to={docPathOverview()}
                  className={`doc-nav-link ${isActivePath(docPathOverview()) ? 'active' : ''}`}
                >
                  Product Overview
                </Link>

                <div className="doc-nav-group">
                  <button
                    type="button"
                    className="doc-nav-group-toggle"
                    onClick={() => toggleSection('getting-started')}
                    aria-expanded={expanded.has('getting-started')}
                  >
                    <span>Getting Started</span>
                    <NavChevron open={expanded.has('getting-started')} />
                  </button>
                  {expanded.has('getting-started') && (
                    <div className="doc-nav-children">
                      <Link
                        to={docPathGettingStarted()}
                        className={`doc-nav-link doc-nav-link-nested ${
                          isActivePath(docPathGettingStarted()) ? 'active' : ''
                        }`}
                      >
                        Setup overview
                      </Link>
                      {GETTING_STARTED.guides.map((guide) => (
                        <Link
                          key={guide.id}
                          to={docPathGettingStartedGuide(guide.id)}
                          className={`doc-nav-link doc-nav-link-nested ${
                            isActivePath(docPathGettingStartedGuide(guide.id)) ? 'active' : ''
                          }`}
                        >
                          <span className="doc-nav-step">{guide.stepNumber}</span>
                          {guide.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <p className="doc-sidebar-label doc-sidebar-label-spaced">Modules</p>

                {DOCUMENTATION_MODULES.map((mod) => {
                  const guides = MODULE_SETUP_GUIDES[mod.id]?.guides || [];
                  const isOpen = expanded.has(mod.id);
                  const moduleOverviewPath = docPathModule(mod.id);
                  return (
                    <div key={mod.id} className="doc-nav-group">
                      <button
                        type="button"
                        className="doc-nav-group-toggle"
                        onClick={() => toggleSection(mod.id)}
                        aria-expanded={isOpen}
                      >
                        <span>
                          <DocModuleIcon moduleId={mod.id} accent={mod.accent} />
                          {mod.title}
                        </span>
                        <NavChevron open={isOpen} />
                      </button>
                      {isOpen && (
                        <div className="doc-nav-children">
                          <Link
                            to={moduleOverviewPath}
                            className={`doc-nav-link doc-nav-link-nested ${
                              isActivePath(moduleOverviewPath) ? 'active' : ''
                            }`}
                          >
                            Setup overview
                          </Link>
                          {guides.map((guide) => (
                            <Link
                              key={guide.id}
                              to={docPathModuleGuide(mod.id, guide.id)}
                              className={`doc-nav-link doc-nav-link-nested ${
                                isActivePath(docPathModuleGuide(mod.id, guide.id)) ? 'active' : ''
                              }`}
                            >
                              <span className="doc-nav-step">{guide.stepNumber}</span>
                              {guide.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </aside>

            <div className="doc-content">{renderContent()}</div>
          </div>

          <p className="text-center text-muted small mt-4 mb-0">
            Questions before signup?{' '}
            <Link to="/contact" className="text-primary fw-semibold">
              Contact our team
            </Link>
            {' '}or explore{' '}
            <Link to="/pricing" className="text-primary fw-semibold">
              pricing plans
            </Link>
            .
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Documentation;
